/**
 * Dialogo "Dati e relazioni" per Good360.
 *
 * Sezioni edit:
 *  - Informazioni prodotto → modifica i dati del Good sottostante (goodPatch).
 *  - Descrizione editoriale / Prodotti correlati (config) / Stato → campi propri del Good360.
 * Le sezioni relazione (Aziende / Trend / Sezioni editoriali / …) e lo staging sono gestiti dalla
 * shell generica `Relations360Dialog`.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ENTITY } from '../utils/entities';
import { client } from '../utils/client';
import { EDIT_SECTION_LABELS } from '../utils/entities/companyData360';
import { getGoodConnectedRef } from '../utils/entities/goodData360';
import { Good360SelfEditQuery } from '../graphql/query/Good360SelfEditQuery';
import { CompanyData360EditView } from './CompanyData360EditView';
import {
	EditorialEditSection,
	RelatedConfigEditSection,
	editorialSummary,
	relatedSummary,
} from './Good360SelfEditSections';
import {
	Good360EvidencesSection,
	evidencesSummary,
} from './Good360EvidencesSection';
import { EditSectionSpec, Relations360Dialog } from './relations360Shared';

interface Good360RelationsModalProps {
	entity: any;
	entityName: ENTITY;
	isOpen: boolean;
	onClose: () => void;
}

export const Good360RelationsModal: React.FC<Good360RelationsModalProps> = ({
	entity,
	entityName,
	isOpen,
	onClose,
}) => {
	const connectedRef = useMemo(() => getGoodConnectedRef(entity), [entity]);
	const [self, setSelf] = useState<any>(null);

	const loadEditData = useCallback(async () => {
		try {
			const r = await client.query<any, any>({
				query: Good360SelfEditQuery,
				variables: { id: entity.id },
				fetchPolicy: 'no-cache',
			});
			setSelf((r.data && r.data.entity) || null);
		} catch (e) {
			setSelf(null);
		}
	}, [entity]);

	useEffect(() => {
		if (isOpen) loadEditData();
	}, [isOpen, loadEditData]);

	const editSections: EditSectionSpec[] = useMemo(() => {
		const specs: EditSectionSpec[] = [];

		// Informazioni prodotto → modifica il Good collegato (se disponibile).
		if (connectedRef) {
			specs.push({
				key: 'info',
				labelKey: EDIT_SECTION_LABELS.info,
				summaryText: (self && self.Good && self.Good.name) || '—',
				render: (onSaved: () => void) => (
					<CompanyData360EditView
						config={connectedRef.config}
						sectionKey="info"
						connectedId={connectedRef.id}
						company360Id={entity.id}
						summary={self}
						onSaved={onSaved}
					/>
				),
			});
		}

		specs.push(
			{
				key: 'editorial',
				labelKey: 'Good360/section/editorial',
				summaryText: editorialSummary(self),
				render: (onSaved) => (
					<EditorialEditSection good360Id={entity.id} onSaved={onSaved} />
				),
			},
			{
				key: 'related',
				labelKey: 'Good360/section/related',
				summaryText: relatedSummary(self),
				render: (onSaved) => (
					<RelatedConfigEditSection good360Id={entity.id} onSaved={onSaved} />
				),
			},
			{
				key: 'evidences',
				labelKey: 'Good360/section/evidences',
				summaryText: evidencesSummary(self && self.evidences),
				render: (onSaved) => (
					<Good360EvidencesSection good360Id={entity.id} onSaved={onSaved} />
				),
			},
		);

		return specs;
	}, [connectedRef, self, entity]);

	return (
		<Relations360Dialog
			entity={entity}
			entityName={entityName}
			isOpen={isOpen}
			onClose={onClose}
			editSections={editSections}
			onReloadEdit={loadEditData}
		/>
	);
};